/**
 * Factor trees. The same toy serves a seven-year-old splitting 12 into 2 x 2 x 3
 * and an older student pulling 2431 apart into 11 x 13 x 17, or checking that
 * 8191 is prime.
 *
 * Pure arithmetic, no canvas.
 */

/**
 * Trial division only has to reach the square root, so eight digits costs a
 * few thousand steps — nothing. The real ceiling is how deep a factor tree can
 * be drawn: 8,388,608 is two to the twenty-third, and twenty-three rungs is
 * about as many as will fit down a phone.
 */
export const MAX_FACTOR_TARGET = 9999999;

/** Smallest prime that divides n, or null when n is prime (or below 2). */
export function smallestPrimeFactor(n: number): number | null {
  if (!Number.isInteger(n) || n < 4) return null;
  if (n % 2 === 0) return 2;
  const limit = Math.floor(Math.sqrt(n));
  for (let d = 3; d <= limit; d += 2) {
    if (n % d === 0) return d;
  }
  return null;
}

export function isPrime(n: number): boolean {
  if (!Number.isInteger(n) || n < 2) return false;
  if (n < 4) return true;
  return smallestPrimeFactor(n) === null;
}

/** Every prime factor with repeats, smallest first: 360 -> [2,2,2,3,3,5]. */
export function primeFactors(n: number): number[] {
  if (!Number.isInteger(n) || n < 2) return [];
  const out: number[] = [];
  let rest = n;
  let d = 2;
  while (d * d <= rest) {
    while (rest % d === 0) {
      out.push(d);
      rest /= d;
    }
    d += d === 2 ? 1 : 2;
  }
  if (rest > 1) out.push(rest);
  return out;
}

export interface PrimePower {
  prime: number;
  power: number;
}

export function primePowers(n: number): PrimePower[] {
  const out: PrimePower[] = [];
  for (const p of primeFactors(n)) {
    const last = out[out.length - 1];
    if (last && last.prime === p) last.power++;
    else out.push({ prime: p, power: 1 });
  }
  return out;
}

/** How many whole numbers divide n, straight from the exponents. */
export function divisorCount(n: number): number {
  if (!Number.isInteger(n) || n < 1) return 0;
  return primePowers(n).reduce((total, { power }) => total * (power + 1), 1);
}

export function divisors(n: number): number[] {
  if (!Number.isInteger(n) || n < 1) return [];
  const out: number[] = [];
  const limit = Math.floor(Math.sqrt(n));
  for (let d = 1; d <= limit; d++) {
    if (n % d === 0) {
      out.push(d);
      if (d !== n / d) out.push(n / d);
    }
  }
  return out.sort((a, b) => a - b);
}

/** "2 x 2 x 2 x 3 x 3 x 5" */
export function factorString(n: number): string {
  const factors = primeFactors(n);
  return factors.length ? factors.join(' x ') : String(n);
}

/** "2^3 x 3^2 x 5" — the shorthand an older student wants. */
export function powerString(n: number): string {
  const powers = primePowers(n);
  if (powers.length === 0) return String(n);
  return powers.map(({ prime, power }) => (power === 1 ? String(prime) : `${prime}^${power}`)).join(' x ');
}

/** The state of a tree being pulled apart, one prime at a time. */
export interface FactorTree {
  target: number;
  /** Primes pulled out so far, in the order they were taken. */
  taken: number[];
}

export function createTree(target: number): FactorTree {
  return { target, taken: [] };
}

/** What is still to be split. */
export function treeRemainder(tree: FactorTree): number {
  return tree.taken.reduce((rest, p) => rest / p, tree.target);
}

export function canSplit(tree: FactorTree): boolean {
  return smallestPrimeFactor(treeRemainder(tree)) !== null;
}

/** Pull out the smallest prime factor of what is left. */
export function splitOnce(tree: FactorTree): FactorTree {
  const p = smallestPrimeFactor(treeRemainder(tree));
  if (p === null) return tree;
  return { ...tree, taken: [...tree.taken, p] };
}

export function splitAll(tree: FactorTree): FactorTree {
  let current = tree;
  // primeFactors of a value below MAX_FACTOR_TARGET has at most ~20 entries.
  for (let i = 0; i < 64 && canSplit(current); i++) current = splitOnce(current);
  return current;
}

export function isTreeComplete(tree: FactorTree): boolean {
  return tree.target >= 2 && !canSplit(tree);
}

/** One plain line about where the tree has got to. */
export function factorHint(tree: FactorTree): string {
  const { target } = tree;
  if (target < 2) return 'Pick a number bigger than 1';
  const rest = treeRemainder(tree);

  if (isTreeComplete(tree)) {
    if (tree.taken.length === 0) return `${target} is prime. Only 1 and ${target} divide it.`;
    const powers = primePowers(target);
    const shape = powers.map(({ power }) => `(${power} + 1)`).join(' x ');
    return `${shape} = ${divisorCount(target)} divisors altogether`;
  }

  const p = smallestPrimeFactor(rest);
  if (p === null) return `${rest} is prime`;
  return `${p} goes into ${rest}, and ${rest} ÷ ${p} = ${rest / p}`;
}
